/** @jsx jsx */
import { Fragment } from 'react'
import { GatsbyImage } from 'gatsby-plugin-image'
import { Box, Container, Heading, jsx } from 'theme-ui'
import SideBySideGrid from '~/components/Content/Section/SideBySideGrid'
import SectionBackgroundAccent from '~/assets/images/backgrounds/section-background-accent.inline.svg'
import SimpleContactForm from '~/components/Content/Forms/SimpleContactForm'

const SideBySideSection = ({
  slug,
  title,
  description,
  cta,
  footer,
  media,
  image,
  bgOverride,
  variant,
}) => {
  let outerPt = 4
  let outerPb = 5
  let descriptionVariant = 'text.body1'
  let footerSx = {
    variant: 'text.body1',
    h3: {
      variant: 'text.title3',
      mb: 1,
    },
    'p:first-of-type': {
      mt: 0,
    },
    'p:last-of-type': {
      mb: 0,
    },
    a: {
      variant: 'text.link',
    },
  }
  let backgroundAccentSx = {
    transform: 'translateX(calc(-50% + 80px))',
  }

  if (variant === 'form') {
    outerPt = [5, 6, null, 8]
    outerPb = [6, 7, null, 9]
    backgroundAccentSx = {
      display: 'none',
    }
  }

  if (variant === 'quote') {
    descriptionVariant = 'text.quote2'
    footerSx = { variant: 'text.callout1' }
    backgroundAccentSx = {
      transform: 'scale(-1, 1) translateX(calc(50% + 80px))',
    }
  }

  return (
    <Box mb={-4} pb={4} sx={{ overflow: 'hidden' }}>
      <Box sx={{ position: 'relative' }}>
        <Box bg={bgOverride || 'backgroundMedium'} pt={outerPt} pb={outerPb}>
          <Container>
            <SideBySideGrid
              padSecond={false}
              swapOrder={variant === 'generic'}
              first={
                <Box
                  pt={[2, 3]}
                  pr={variant === 'form' && [0, null, 6]}
                  sx={{ textAlign: ['center', 'left'] }}
                >
                  {title && (
                    <Heading as="h2" variant="title2" mb={2}>
                      {title}
                    </Heading>
                  )}
                  {description && (
                    <Box
                      sx={{
                        variant: descriptionVariant,
                        'p:first-of-type': {
                          mt: 0,
                        },
                        'p:last-of-type': {
                          mb: 0,
                        },
                        a: {
                          variant: 'text.link',
                        },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: description.childMarkdownRemark.html,
                      }}
                    />
                  )}
                  {cta && (
                    <Box
                      mt={[3, 4]}
                      mb={[2, 3]}
                      sx={{
                        a: {
                          textAlign: 'center',
                          width: ['100%', null, 'auto'],
                          '&:nth-of-type(1)': {
                            mb: 2,
                            mr: 2,
                          },
                          '&:nth-of-type(2)': {
                            variant: 'buttons.secondary',
                            background: 'white',
                          },
                        },
                      }}
                    >
                      {cta}
                    </Box>
                  )}
                  {footer && (
                    <Box
                      mt={2}
                      sx={footerSx}
                      dangerouslySetInnerHTML={{
                        __html: footer.childMarkdownRemark.html,
                      }}
                    />
                  )}
                  {variant !== 'form' &&
                    media &&
                    media.map((item) => (
                      <Box mt={[2, 3]} key={`side-by-side-${item.id}`}>
                        <SideBySideSectionMedia item={item} slug={slug} />
                      </Box>
                    ))}
                </Box>
              }
              second={
                <Fragment>
                  {image && (
                    <Box px={[5, 0, 6]}>
                      <GatsbyImage
                        image={image.gatsbyImageData}
                        alt={image.description}
                      />
                    </Box>
                  )}
                  {variant === 'form' &&
                    media &&
                    media.map((item) => (
                      <Box
                        mt={[2, 3]}
                        px={[5, 0]}
                        key={`side-by-side-${item.id}`}
                      >
                        <SideBySideSectionMedia item={item} slug={slug} />
                      </Box>
                    ))}
                </Fragment>
              }
            />
          </Container>
        </Box>
        <Box
          color={bgOverride || 'backgroundMedium'}
          sx={{
            background: 'transparent',
            bottom: '-32px',
            height: '32px',
            left: '50%',
            pointerEvents: 'none',
            position: 'absolute',
            width: ['1500px', '2000px'],
            zIndex: '1',
            ...backgroundAccentSx,
          }}
        >
          <SectionBackgroundAccent />
        </Box>
      </Box>
    </Box>
  )
}

const SideBySideSectionMedia = ({ item, slug }) => {
  const { __typename, heading } = item

  if (__typename === 'ContentfulForm') {
    return <SimpleContactForm name={slug} heading={heading} />
  }

  return <Fragment />
}

export default SideBySideSection
